import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { PERMISSION_LABELS, PERMISSION_PRIMITIVES, isAppKey, type AppKey } from '@qms360/shared';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { PageMessage } from '../AppHome.js';
import { ApiError } from '../../lib/api.js';
import { useSession } from '../../lib/session.js';
import { ScopeFields, describeScope, useScopeLists, type ScopeValue } from '../../components/admin/ScopeFields.js';
import {
  bulkGrants,
  createAccount,
  createDelegation,
  createWorkspaceRole,
  updateGrantScope,
  updateRolePermissions,
  fetchRbac,
  grantRole,
  resetPassword,
  revokeDelegation,
  revokeGrant,
  setPlatformRole,
  type BulkGrantReport,
  type CreatedAccount,
  type DirectoryUser,
  type PasswordResetResult,
  type RbacSnapshot,
} from '../../lib/rbac-admin-api.js';
import { t, formatDate, formatDateTime } from '../../lib/i18n.js';

/**
 * RBAC & User Management (spec §13.1).
 *
 * Until this screen existed, granting somebody a role meant re-seeding the
 * database — the one authority everything else rests on was the only thing an
 * administrator could not change.
 *
 * Three things it is careful to show rather than hide:
 *
 *  - **An expired grant stays visible, marked expired.** "This exists and does
 *    nothing" is exactly what somebody needs to see in order to renew it.
 *  - **A system role is shown as uneditable with the reason**, not omitted. The
 *    shipped roles are transcribed from spec §6; the answer is a custom role
 *    beside them (ADR-0005), and the screen says so.
 *  - **A bulk upload previews by default.** The validated result is what you get
 *    unless you ask for the opposite, because this is the fastest way in QMS360
 *    to give the wrong people access to the wrong projects.
 */
export function AccessAdmin() {
  const params = useParams<{ app: string }>();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const rbac = useQuery({ queryKey: ['rbac'], queryFn: fetchRbac });

  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['rbac'] });
  };
  const fail = (fallback: string) => (caught: unknown) =>
    setError(caught instanceof ApiError ? caught.message : fallback);

  const platform = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string | null }) =>
      setPlatformRole(userId, roleId),
    onSuccess: refresh,
    onError: fail(t("That platform role could not be changed.")),
  });

  const grant = useMutation({
    mutationFn: (body: Record<string, unknown>) => grantRole(body),
    onSuccess: refresh,
    onError: fail(t("That role could not be granted.")),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeGrant(id),
    onSuccess: refresh,
    onError: fail(t("That role could not be revoked.")),
  });

  const role = useMutation({
    mutationFn: (body: Record<string, unknown>) => createWorkspaceRole(body),
    onSuccess: refresh,
    onError: fail(t("That role could not be created.")),
  });

  const changeScope = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => updateGrantScope(id, body),
    onSuccess: refresh,
    onError: fail(t("That scope could not be changed.")),
  });

  const editRole = useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: Record<string, { scope: string }> }) =>
      updateRolePermissions(id, permissions as never),
    onSuccess: refresh,
    onError: fail(t("That role could not be changed.")),
  });

  const delegate = useMutation({
    mutationFn: createDelegation,
    onSuccess: refresh,
    onError: fail(t("That delegation could not be created.")),
  });

  const endDelegation = useMutation({
    mutationFn: (id: string) => revokeDelegation(id),
    onSuccess: refresh,
    onError: fail(t("That delegation could not be revoked.")),
  });

  const app = params.app;
  if (!app || !isAppKey(app)) {
    return <PageMessage title={t("Unknown application")} body="That application does not exist." />;
  }

  const data = rbac.data;

  return (
    <div className="min-h-full">
      <AppTopBar app={app as AppKey} />
      <Breadcrumbs app={app as AppKey} trail={['Settings', 'Users & roles']} />

      <main className="mx-auto max-w-5xl px-4 pb-16 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Users & roles")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("A revocation takes effect on the person’s")}{' '}<strong>{t("next request")}</strong>{' '}{t("— nobody has to sign out, because authority is read from here every time rather than carried in a token.")}
        </p>

        {error && (
          <p role="alert" className="mt-4 text-sm" style={{ color: 'var(--tone-rejected)' }}>
            {error}
          </p>
        )}

        {rbac.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}

        {rbac.error && (
          <section className="mt-6 rounded-md border border-border bg-tone-info-surface p-6" role="status">
            <h2 className="m-0 text-base font-semibold">{t("Not available to your role")}</h2>
            <p className="mb-0 mt-2 text-sm text-content-secondary">
              {t("Administering access is a distinct authority from configuring master data, held by administrators only.")}
            </p>
          </section>
        )}

        {data && (
          <>
            <NewAccount data={data} onCreated={refresh} />

            <Directory
              data={data}
              busy={platform.isPending || grant.isPending || revoke.isPending || changeScope.isPending}
              onPlatformRole={(userId, roleId) => platform.mutate({ userId, roleId })}
              onGrant={(body) => grant.mutate(body)}
              onRevoke={(id) => revoke.mutate(id)}
              onChangeScope={(id, body) => changeScope.mutate({ id, body })}
            />

            <RoleBuilder
              data={data}
              busy={role.isPending || editRole.isPending}
              onCreate={(body) => role.mutate(body)}
              onEdit={(id, permissions) => editRole.mutate({ id, permissions })}
            />

            <DelegationRegister
              data={data}
              busy={endDelegation.isPending || delegate.isPending}
              onRevoke={(id) => endDelegation.mutate(id)}
              onCreate={(body) => delegate.mutate(body)}
            />

            <BulkUpload onError={setError} onApplied={refresh} />

            <History data={data} />
          </>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Reset a local account's password (spec §2: dev and UAT sign-in only).
 *
 * Two clicks, not one, because it signs the person out everywhere at once.
 * The temporary password is shown once, here, and nowhere else: the server
 * keeps only its hash and leaves it out of the audit log. Leaving the page
 * loses it, which is the point — a new reset is the way to get another.
 *
 * Not offered on your own row, and not for platform-authenticated accounts —
 * the API refuses both, and a button that can only fail is not worth showing.
 */
function PasswordReset({ user }: { user: DirectoryUser }) {
  const { user: me } = useSession();
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<PasswordResetResult | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = useMutation({
    mutationFn: () => resetPassword(user.id),
    onSuccess: (outcome) => {
      setResult(outcome);
      setConfirming(false);
    },
  });

  if (user.authSource !== 'local' || user.id === me?.id) return null;

  if (result) {
    return (
      <div role="status" className="mt-3 rounded border border-border bg-tone-pending-surface p-3 text-xs">
        <p className="m-0">
          {t("Temporary password for")}{' '}<strong>{result.user.displayName}</strong>{' '}{t("— shown once. They must choose a new one when they sign in")}
          {result.revokedSessions > 0
            ? t(", and {0} open session{1} signed out.", { 0: result.revokedSessions, 1: result.revokedSessions === 1 ? ' was' : 's were' })
            : '.'}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <code className="select-all rounded bg-surface px-2 py-1 text-sm" data-testid="temporary-password">
            {result.temporaryPassword}
          </code>
          <button
            type="button"
            className="rounded border border-border px-2 py-0.5"
            onClick={() => {
              void navigator.clipboard?.writeText(result.temporaryPassword).then(() => setCopied(true));
            }}
          >
            {copied ? t("Copied") : t("Copy")}
          </button>
          <button type="button" className="rounded border border-border px-2 py-0.5" onClick={() => setResult(null)}>
            {t("Done")}
          </button>
        </div>
      </div>
    );
  }

  if (confirming) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <span>
          {t("Reset")}{' '}{user.displayName}{t("’s password? This signs them out everywhere.")}
        </span>
        <button
          type="button"
          className="rounded bg-ag-purple px-2 py-0.5 text-white"
          disabled={reset.isPending}
          onClick={() => reset.mutate()}
        >
          {reset.isPending ? t("Resetting…") : t("Yes, reset")}
        </button>
        <button type="button" className="rounded border border-border px-2 py-0.5" onClick={() => setConfirming(false)}>
          {t("Cancel")}
        </button>
        {reset.error && (
          <span role="alert" style={{ color: 'var(--tone-rejected)' }}>
            {reset.error instanceof ApiError ? reset.error.message : t("The password could not be reset.")}
          </span>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      className="ms-2 mt-2 rounded border border-border px-2 py-0.5 text-xs"
      onClick={() => setConfirming(true)}
    >
      {t("Reset password")}
    </button>
  );
}

/**
 * A new account (spec §13.1): most often a Guest Auditor from outside Algihaz,
 * who then needs inviting to an audit from that audit. The temporary password is
 * shown once, like a reset's, and must be changed at first sign-in.
 */
function NewAccount({ data, onCreated }: { data: RbacSnapshot; onCreated: () => void }) {
  const guestRole = data.platformRoles.find((role) => role.key === 'guest_auditor');
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [platformRoleId, setPlatformRoleId] = useState(guestRole?.id ?? '');
  const [result, setResult] = useState<CreatedAccount | null>(null);
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: () => createAccount({ username: username.trim(), email: email.trim(), displayName: displayName.trim(), platformRoleId }),
    onSuccess: (outcome) => {
      setResult(outcome);
      setOpen(false);
      setUsername('');
      setEmail('');
      setDisplayName('');
      onCreated();
    },
  });

  return (
    <Panel
      heading={t("New account")}
      note={t("For someone who signs in to QMS360 directly — usually an external Guest Auditor. A Guest Auditor then needs inviting to an audit, from that audit.")}
    >
      {result && (
        <div role="status" className="mb-3 rounded border border-border bg-tone-pending-surface p-3 text-xs">
          <p className="m-0">
            {t("Account created for")}{' '}<strong>{result.user.displayName}</strong> ({result.user.email}{t("). Temporary password — shown once. They must choose a new one when they sign in.")}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="select-all rounded bg-surface px-2 py-1 text-sm" data-testid="new-account-password">
              {result.temporaryPassword}
            </code>
            <button
              type="button"
              className="rounded border border-border px-2 py-0.5"
              onClick={() => {
                void navigator.clipboard?.writeText(result.temporaryPassword).then(() => setCopied(true));
              }}
            >
              {copied ? t("Copied") : t("Copy")}
            </button>
            <button type="button" className="rounded border border-border px-2 py-0.5" onClick={() => setResult(null)}>
              {t("Done")}
            </button>
          </div>
        </div>
      )}

      {!open ? (
        <button type="button" className="rounded border border-border px-3 py-1.5 text-sm" onClick={() => setOpen(true)}>
          {t("Create an account")}
        </button>
      ) : (
        <form
          aria-label={t("New account")}
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <label className="block text-sm">
            {t("Name")}
            <input className="mt-1 block w-full rounded border border-border px-2 py-1.5" value={displayName} onChange={(event) => setDisplayName(event.target.value)} required />
          </label>
          <label className="block text-sm">
            {t("Email")}
            <input type="email" className="mt-1 block w-full rounded border border-border px-2 py-1.5" value={email} onChange={(event) => setEmail(event.target.value)} required />
          </label>
          <label className="block text-sm">
            {t("Username")}
            <input className="mt-1 block w-full rounded border border-border px-2 py-1.5" value={username} onChange={(event) => setUsername(event.target.value)} required />
          </label>
          <label className="block text-sm">
            {t("Platform role")}
            <select className="mt-1 block w-full rounded border border-border px-2 py-1.5" value={platformRoleId} onChange={(event) => setPlatformRoleId(event.target.value)} required>
              <option value="">{t("Choose a role")}</option>
              {data.platformRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {t(role.label)}
                </option>
              ))}
            </select>
          </label>
          {create.error && (
            <p role="alert" className="m-0 text-sm sm:col-span-2" style={{ color: 'var(--tone-rejected)' }}>
              {create.error instanceof ApiError ? create.error.message : t("The account could not be created.")}
            </p>
          )}
          <div className="flex gap-2 sm:col-span-2">
            <button type="submit" className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white disabled:opacity-60" disabled={create.isPending}>
              {create.isPending ? t("Creating…") : t("Create account")}
            </button>
            <button type="button" className="rounded border border-border px-3 py-1.5 text-sm" onClick={() => setOpen(false)}>
              {t("Cancel")}
            </button>
          </div>
        </form>
      )}
    </Panel>
  );
}

function Panel({ heading, note, children }: { heading: string; note: string; children: React.ReactNode }) {
  return (
    <section className="mt-8 rounded-md border border-border bg-surface p-5">
      <h2 className="m-0 text-lg font-semibold">{heading}</h2>
      <p className="mt-1 text-sm text-content-secondary">{note}</p>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Directory({
  data,
  busy,
  onPlatformRole,
  onGrant,
  onRevoke,
  onChangeScope,
}: {
  data: RbacSnapshot;
  busy: boolean;
  onPlatformRole: (userId: string, roleId: string | null) => void;
  onGrant: (body: Record<string, unknown>) => void;
  onRevoke: (id: string) => void;
  onChangeScope: (grantId: string, body: Record<string, unknown>) => void;
}) {
  const [openUser, setOpenUser] = useState<string | null>(null);
  const [roleId, setRoleId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [scope, setScope] = useState<ScopeValue>({ businessUnitIds: [], projectIds: [] });
  /** The grant whose scope is being changed, and its draft. */
  const [editing, setEditing] = useState<{ id: string; app: string; scope: ScopeValue; expiresAt: string } | null>(null);
  const chosenRole = data.workspaceRoles.find((role) => role.id === roleId);

  return (
    <Panel
      heading={t("User directory")}
      note={t("Platform Role, Workspace Roles and their scope, and when each person last signed in.")}
    >
      <ul className="m-0 list-none space-y-3 p-0">
        {data.users.map((user) => (
          <li key={user.id} className="rounded border border-border p-3 text-sm">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span>
                <strong>{user.displayName}</strong>{' '}
                <span className="text-content-secondary">{user.username}</span>
              </span>
              <span className="text-xs text-content-secondary">
                {user.lastAccessAt
                  ? t("last seen {0}", { 0: formatDate(user.lastAccessAt) })
                  : t("never signed in")}
              </span>
            </div>

            <label className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-content-secondary">{t("Platform role")}</span>
              <select
                className="rounded border border-border px-2 py-1"
                value={user.platformRoleId ?? ''}
                disabled={busy}
                onChange={(event) => onPlatformRole(user.id, event.target.value || null)}
              >
                <option value="">{t("None")}</option>
                {data.platformRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {t(role.label)}
                  </option>
                ))}
              </select>
            </label>

            <ul className="mt-2 list-none space-y-1 p-0">
              {user.workspaceRoles.length === 0 && (
                <li className="text-xs text-content-secondary">
                  {t("No workspace role — this person reaches an app home screen and sees no modules.")}
                </li>
              )}
              {user.workspaceRoles.map((assignment) => (
                <li key={assignment.id} className="flex flex-wrap items-center gap-2 text-xs">
                  <span>
                    {t(assignment.label)} <span className="text-content-secondary">({assignment.app})</span>{' '}
                    <GrantScope app={assignment.app} scope={assignment} />
                    {assignment.expiresAt && !assignment.isExpired && (
                      <span className="text-content-secondary">{' '}{t("· until")}{' '}{formatDate(assignment.expiresAt)}</span>
                    )}
                  </span>
                  {assignment.isExpired && (
                    /* Shown, not hidden: "this grant exists and does nothing" is
                       what somebody needs in order to renew it. */
                    <span className="rounded-full bg-tone-critical-surface px-2 py-0.5">
                      {t("expired")}{' '}{formatDate(assignment.expiresAt!)}
                    </span>
                  )}
                  <button
                    type="button"
                    className="rounded border border-border px-2 py-0.5"
                    disabled={busy}
                    onClick={() =>
                      setEditing({
                        id: assignment.id,
                        app: assignment.app,
                        scope: { businessUnitIds: assignment.businessUnitIds, projectIds: assignment.projectIds },
                        expiresAt: assignment.expiresAt ? assignment.expiresAt.slice(0, 10) : '',
                      })
                    }
                  >
                    {t("Change scope")}
                  </button>
                  <button
                    type="button"
                    className="rounded border border-border px-2 py-0.5"
                    disabled={busy}
                    onClick={() => onRevoke(assignment.id)}
                  >
                    {t("Revoke")}
                  </button>
                  {editing?.id === assignment.id && (
                    <form
                      aria-label={t("Change the scope of {0}", { 0: assignment.label })}
                      className="mt-1 grid w-full gap-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        onChangeScope(assignment.id, {
                          businessUnitIds: editing.scope.businessUnitIds,
                          projectIds: editing.scope.projectIds,
                          expiresAt: editing.expiresAt || null,
                        });
                        setEditing(null);
                      }}
                    >
                      <ScopeFields
                        app={editing.app}
                        legend="Reaches"
                        value={editing.scope}
                        onChange={(next) => setEditing({ ...editing, scope: next })}
                      />
                      <label className="flex flex-col">
                        <span className="mb-1 text-content-secondary">{t("Ends (optional — the end of that day, organisation time)")}</span>
                        <input
                          type="date"
                          className="w-48 rounded border border-border px-2 py-1"
                          value={editing.expiresAt}
                          onChange={(event) => setEditing({ ...editing, expiresAt: event.target.value })}
                        />
                      </label>
                      <div className="flex gap-2">
                        <button type="submit" className="rounded bg-ag-purple px-3 py-1 text-white" disabled={busy}>
                          {t("Save scope")}
                        </button>
                        <button type="button" className="rounded border border-border px-3 py-1" onClick={() => setEditing(null)}>
                          {t("Cancel")}
                        </button>
                      </div>
                    </form>
                  )}
                </li>
              ))}
            </ul>

            {openUser === user.id ? (
              <form
                className="mt-2 flex flex-wrap items-end gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  onGrant({
                    userId: user.id,
                    workspaceRoleId: roleId,
                    businessUnitIds: scope.businessUnitIds,
                    projectIds: scope.projectIds,
                    // A date: the server ends it as that day ends where the
                    // organisation is (DATA-13).
                    ...(expiresAt ? { expiresAt } : {}),
                  });
                  setOpenUser(null);
                  setRoleId('');
                  setExpiresAt('');
                  setScope({ businessUnitIds: [], projectIds: [] });
                }}
              >
                <label className="flex flex-col text-xs">
                  <span className="mb-1 text-content-secondary">{t("Workspace role")}</span>
                  <select
                    className="rounded border border-border px-2 py-1"
                    value={roleId}
                    onChange={(event) => setRoleId(event.target.value)}
                  >
                    <option value="">{t("Choose…")}</option>
                    {data.workspaceRoles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {t(role.label)} ({role.app})
                      </option>
                    ))}
                  </select>
                </label>
                {chosenRole?.app && (
                  <div className="w-full">
                    <ScopeFields app={chosenRole.app} legend="Reaches" value={scope} onChange={setScope} />
                  </div>
                )}
                <label className="flex flex-col text-xs">
                  <span className="mb-1 text-content-secondary">{t("Ends (optional — the end of that day, organisation time)")}</span>
                  <input
                    type="date"
                    className="rounded border border-border px-2 py-1"
                    value={expiresAt}
                    onChange={(event) => setExpiresAt(event.target.value)}
                  />
                </label>
                <button
                  type="submit"
                  className="rounded bg-ag-purple px-3 py-1 text-xs text-white"
                  disabled={!roleId || busy}
                >
                  {t("Grant")}
                </button>
              </form>
            ) : (
              <button
                type="button"
                className="mt-2 rounded border border-border px-2 py-0.5 text-xs"
                onClick={() => setOpenUser(user.id)}
              >
                {t("Grant a role")}
              </button>
            )}

            <PasswordReset user={user} />
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** A grant's reach, in names. */
function GrantScope({ app, scope }: { app: string; scope: { businessUnitIds: string[]; projectIds: string[] } }) {
  const lists = useScopeLists(app);
  return <span className="text-content-secondary">{t("· reaches")}{' '}{describeScope(app, scope, lists)}</span>;
}

function RoleBuilder({
  data,
  busy,
  onCreate,
  onEdit,
}: {
  data: RbacSnapshot;
  busy: boolean;
  onCreate: (body: Record<string, unknown>) => void;
  onEdit: (id: string, permissions: Record<string, { scope: string }>) => void;
}) {
  /** A custom role being edited, and its draft permissions. */
  const [editing, setEditing] = useState<{ id: string; permissions: Record<string, { scope: string }> } | null>(null);
  const [app, setApp] = useState<'qaqc' | 'lesson-learned' | 'audit'>('qaqc');
  const [label, setLabel] = useState('');
  const [permissions, setPermissions] = useState<Record<string, { scope: string }>>({});

  const setScope = (primitive: string, scope: string) =>
    setPermissions((current) => {
      const next = { ...current };
      if (scope === 'none') delete next[primitive];
      else next[primitive] = { scope };
      return next;
    });

  return (
    <Panel
      heading={t("Role builder")}
      note={t("A custom role is built from the same permissions as the shipped roles and nothing else, so it is checked exactly the way they are.")}
    >
      <ul className="m-0 mb-4 list-none space-y-1 p-0 text-sm">
        {data.workspaceRoles.map((role) => (
          <li key={role.id} className="flex flex-wrap items-center gap-2">
            <span>
              {t(role.label)} <span className="text-content-secondary">({role.app})</span>
            </span>
            {role.isSystem ? (
              /* Shown with the reason rather than omitted — otherwise the
                 absence of an Edit button looks like a bug. */
              <span className="rounded-full bg-surface-sunken px-2 py-0.5 text-xs text-content-secondary">
                {t("shipped — not editable; build a custom role beside it")}
              </span>
            ) : (
              <button
                type="button"
                className="rounded border border-border px-2 py-0.5 text-xs"
                onClick={() =>
                  setEditing({
                    id: role.id,
                    permissions: Object.fromEntries(
                      Object.entries(role.permissions).map(([key, grant]) => [key, { scope: grant.scope }]),
                    ),
                  })
                }
              >
                {t("Edit permissions")}
              </button>
            )}
            {editing?.id === role.id && (
              <form
                aria-label={t("Edit {0}", { 0: role.label })}
                className="mt-1 w-full rounded border border-border p-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  onEdit(role.id, editing.permissions);
                  setEditing(null);
                }}
              >
                <p className="m-0 text-xs text-content-secondary">
                  {t("Everybody holding this role gets the change on their next request.")}
                </p>
                <table className="mt-2 w-full text-sm">
                  <caption className="sr-only">{t("Permissions of")}{' '}{t(role.label)}</caption>
                  <tbody>
                    {PERMISSION_PRIMITIVES.map((primitive) => (
                      <tr key={primitive}>
                        <td className="py-1">{t(PERMISSION_LABELS[primitive])}</td>
                        <td className="py-1">
                          <select
                            aria-label={t("{0} for {1}", { 0: PERMISSION_LABELS[primitive], 1: role.label })}
                            className="rounded border border-border px-2 py-1"
                            value={editing.permissions[primitive]?.scope ?? 'none'}
                            onChange={(event) => {
                              const next = { ...editing.permissions };
                              if (event.target.value === 'none') delete next[primitive];
                              else next[primitive] = { scope: event.target.value };
                              setEditing({ ...editing, permissions: next });
                            }}
                          >
                            <option value="none">{t("— not granted")}</option>
                            <option value="own">{t("Own")}</option>
                            <option value="scoped">{t("Select")}</option>
                            <option value="all">{t("Full")}</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 flex gap-2">
                  <button
                    type="submit"
                    className="rounded bg-ag-purple px-3 py-1 text-xs text-white"
                    disabled={busy || Object.keys(editing.permissions).length === 0}
                  >
                    {t("Save permissions")}
                  </button>
                  <button type="button" className="rounded border border-border px-3 py-1 text-xs" onClick={() => setEditing(null)}>
                    {t("Cancel")}
                  </button>
                </div>
              </form>
            )}
          </li>
        ))}
      </ul>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          onCreate({ app, label, permissions });
          setLabel('');
          setPermissions({});
        }}
      >
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col text-sm">
            <span className="mb-1 text-content-secondary">{t("Application")}</span>
            <select
              className="rounded border border-border px-2 py-1"
              value={app}
              onChange={(event) => setApp(event.target.value as typeof app)}
            >
              <option value="qaqc">{t("QA/QC")}</option>
              <option value="lesson-learned">{t("Lesson Learned")}</option>
              <option value="audit">{t("QMS Audit")}</option>
            </select>
          </label>
          <label className="flex flex-1 flex-col text-sm">
            <span className="mb-1 text-content-secondary">{t("Role name")}</span>
            <input
              className="rounded border border-border px-2 py-1"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          </label>
        </div>

        <table className="mt-3 w-full text-sm">
          <caption className="sr-only">{t("Permission matrix for the new role")}</caption>
          <thead>
            <tr>
              <th scope="col" className="text-start font-normal text-content-secondary">
                {t("Permission")}
              </th>
              <th scope="col" className="text-start font-normal text-content-secondary">
                {t("Scope")}
              </th>
            </tr>
          </thead>
          <tbody>
            {PERMISSION_PRIMITIVES.map((primitive) => (
              <tr key={primitive}>
                <td className="py-1">{t(PERMISSION_LABELS[primitive])}</td>
                <td className="py-1">
                  <select
                    aria-label={PERMISSION_LABELS[primitive]}
                    className="rounded border border-border px-2 py-1"
                    value={permissions[primitive]?.scope ?? 'none'}
                    onChange={(event) => setScope(primitive, event.target.value)}
                  >
                    <option value="none">{t("— not granted")}</option>
                    <option value="own">{t("Own")}</option>
                    <option value="scoped">{t("Select")}</option>
                    <option value="all">{t("Full")}</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <button
          type="submit"
          className="mt-3 rounded bg-ag-purple px-4 py-2 text-sm font-medium text-white"
          disabled={busy || !label.trim() || Object.keys(permissions).length === 0}
        >
          {t("Create role")}
        </button>
        {Object.keys(permissions).length === 0 && label.trim() !== '' && (
          <p className="mt-2 mb-0 text-xs text-content-secondary">
            {t("A role granting nothing would be indistinguishable from not having it.")}
          </p>
        )}
      </form>
    </Panel>
  );
}

function DelegationRegister({
  data,
  busy,
  onRevoke,
  onCreate,
}: {
  data: RbacSnapshot;
  busy: boolean;
  onRevoke: (id: string) => void;
  onCreate: (body: Parameters<typeof createDelegation>[0]) => void;
}) {
  const nameFor = (id: string) => data.users.find((user) => user.id === id)?.displayName ?? id;
  const { user: me } = useSession();
  const [open, setOpen] = useState(false);
  const [toUserId, setToUserId] = useState('');
  const [app, setApp] = useState('qaqc');
  const [scope, setScope] = useState<ScopeValue>({ businessUnitIds: [], projectIds: [] });
  const [startsAt, setStartsAt] = useState(new Date().toISOString().slice(0, 10));
  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState('');

  return (
    <Panel
      heading={t("Delegation register")}
      note={t("Who is currently acting for whom, and until when. One click ends any of them — most obviously when the delegator is the person who is away.")}
    >
      {!open ? (
        <button type="button" className="mb-3 rounded border border-border px-3 py-1.5 text-sm" onClick={() => setOpen(true)}>
          {t("New delegation")}
        </button>
      ) : (
        <form
          aria-label={t("New delegation")}
          className="mb-4 grid gap-2 rounded border border-border p-3 text-sm sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            onCreate({ toUserId, app, businessUnitIds: scope.businessUnitIds, projectIds: scope.projectIds, startsAt, endsAt, reason: reason.trim() });
            setOpen(false);
            setToUserId('');
            setReason('');
            setEndsAt('');
          }}
        >
          <p className="m-0 text-xs text-content-secondary sm:col-span-2">
            {t("You hand over")}{' '}<strong>{t("your own")}</strong>{' '}{t("roles in one application for a period — the person acting holds them, narrowed to what you tick, until the end date. It is recorded against your name.")}
          </p>
          <label className="flex flex-col">
            <span className="mb-1 text-content-secondary">{t("Acting for you")}</span>
            <select className="rounded border border-border px-2 py-1" value={toUserId} onChange={(event) => setToUserId(event.target.value)} required>
              <option value="">{t("Choose a person")}</option>
              {data.users
                .filter((user) => user.id !== me?.id)
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName} ({user.username})
                  </option>
                ))}
            </select>
          </label>
          <label className="flex flex-col">
            <span className="mb-1 text-content-secondary">{t("Application")}</span>
            <select className="rounded border border-border px-2 py-1" value={app} onChange={(event) => setApp(event.target.value)}>
              <option value="qaqc">{t("QA/QC")}</option>
              <option value="lesson-learned">{t("Lesson Learned")}</option>
              <option value="audit">{t("QMS Audit")}</option>
            </select>
          </label>
          <div className="sm:col-span-2">
            <ScopeFields app={app} legend="Covers" value={scope} onChange={setScope} />
          </div>
          <label className="flex flex-col">
            <span className="mb-1 text-content-secondary">{t("From")}</span>
            <input type="date" className="rounded border border-border px-2 py-1" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} required />
          </label>
          <label className="flex flex-col">
            <span className="mb-1 text-content-secondary">{t("Until (the end of that day)")}</span>
            <input type="date" className="rounded border border-border px-2 py-1" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} required />
          </label>
          <label className="flex flex-col sm:col-span-2">
            <span className="mb-1 text-content-secondary">{t("Why")}</span>
            <input className="rounded border border-border px-2 py-1" value={reason} onChange={(event) => setReason(event.target.value)} required />
          </label>
          <div className="flex gap-2 sm:col-span-2">
            <button type="submit" className="rounded bg-ag-purple px-3 py-1.5 text-white" disabled={busy || !toUserId || !endsAt || !reason.trim()}>
              {t("Delegate")}
            </button>
            <button type="button" className="rounded border border-border px-3 py-1.5" onClick={() => setOpen(false)}>
              {t("Cancel")}
            </button>
          </div>
        </form>
      )}

      {data.delegations.length === 0 && (
        <p className="m-0 text-sm text-content-secondary">{t("No delegations on record.")}</p>
      )}
      <ul className="m-0 list-none space-y-2 p-0 text-sm">
        {data.delegations.map((row) => (
          <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-border p-3">
            <span>
              {nameFor(row.fromUserId)}{' '}
              {/* Points from giver to receiver, whichever way the page reads (pass 12). */}
              <span aria-hidden className="rtl-flip inline-block">→</span>
              <span className="sr-only">{t("to")}</span>{' '}
              {nameFor(row.toUserId)}
              <span className="ms-2 text-xs text-content-secondary">
                {formatDate(row.startsAt)}{' '}{t("to")}{' '}
                {formatDate(row.endsAt)}
                {row.reason ? ` · ${row.reason}` : ''}
              </span>
            </span>
            {row.revokedAt ? (
              <span className="text-xs text-content-secondary">
                {t("revoked")}{' '}{formatDate(row.revokedAt)}
              </span>
            ) : (
              <button
                type="button"
                className="rounded border border-border px-2 py-0.5 text-xs"
                disabled={busy}
                onClick={() => onRevoke(row.id)}
              >
                {t("Revoke")}
              </button>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function BulkUpload({
  onError,
  onApplied,
}: {
  onError: (message: string) => void;
  onApplied: () => void;
}) {
  const [csv, setCsv] = useState('');
  const [report, setReport] = useState<BulkGrantReport | null>(null);

  const run = useMutation({
    mutationFn: ({ dryRun }: { dryRun: boolean }) => bulkGrants(csv, dryRun),
    onSuccess: (result) => {
      setReport(result.report);
      if (result.report.applied) onApplied();
    },
    // Say WHY the file could not be read (FN-2): the server sends each problem.
    onError: (caught) => {
      if (!(caught instanceof ApiError)) return onError(t("That file could not be read."));
      const problems = (caught.details as { problems?: string[] } | undefined)?.problems ?? [];
      onError(problems.length ? `${caught.message} ${problems.join(' ')}` : caught.message);
    },
  });

  return (
    <Panel
      heading={t("Bulk role assignment")}
      note={t("Columns: username, roleKey, businessUnitCodes, projectCodes, expiresAt. roleKey is a shipped role's key or a custom role's name. The two code columns are semicolon-separated. expiresAt is a date — access ends as that day ends, organisation time.")}
    >
      <label className="flex flex-col text-sm">
        <span className="mb-1 text-content-secondary">{t("Paste the CSV")}</span>
        <textarea
          className="rounded border border-border px-2 py-1 font-mono text-xs"
          rows={5}
          value={csv}
          onChange={(event) => setCsv(event.target.value)}
          placeholder={t("username,roleKey,businessUnitCodes,projectCodes,expiresAt\nrami,form_creator,CONST,ALPHA;BETA,")}
        />
      </label>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded bg-ag-purple px-4 py-2 text-sm font-medium text-white"
          disabled={!csv.trim() || run.isPending}
          onClick={() => run.mutate({ dryRun: true })}
        >
          {t("Validate")}
        </button>
        <button
          type="button"
          className="rounded border border-border px-4 py-2 text-sm"
          // Applying is only offered once a preview has come back clean, so the
          // destructive option is never the first one available.
          disabled={!report || report.invalid > 0 || report.applied || run.isPending}
          onClick={() => run.mutate({ dryRun: false })}
        >
          {t("Apply")}{' '}{report && report.invalid === 0 ? t("{0} grant(s)", { 0: report.valid }) : ''}
        </button>
      </div>

      {report && (
        <div className="mt-4">
          <p className="m-0 text-sm">
            {report.valid}{' '}{t("valid,")}{' '}{report.invalid}{' '}{t("with problems.")}{' '}
            {report.applied ? (
              <strong>{t("Applied.")}</strong>
            ) : report.invalid > 0 ? (
              /* All or nothing: a half-applied file leaves nobody able to tell
                 which half, and the natural fix double-grants what worked. */
              <span>{t("Nothing was applied — one bad row stops the whole file.")}</span>
            ) : (
              <span>{t("Nothing applied yet.")}</span>
            )}
          </p>

          <ul className="mt-2 list-none space-y-1 p-0 text-xs">
            {report.rows.map((row) => (
              <li key={row.line}>
                <span className="text-content-secondary">{t("line")}{' '}{row.line}</span> {row.username}{' '}
                <span aria-hidden className="rtl-flip inline-block">→</span>{' '}
                {row.roleKey}
                {row.problems.length > 0 && (
                  <span style={{ color: 'var(--tone-rejected)' }}> — {row.problems.join(' ')}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

function History({ data }: { data: RbacSnapshot }) {
  return (
    <Panel
      heading={t("Change history")}
      note={t("A view of the audit log, not a second copy of it — there is one history in QMS360 and this is it, filtered to access changes.")}
    >
      {data.history.length === 0 && (
        <p className="m-0 text-sm text-content-secondary">{t("Nothing recorded yet.")}</p>
      )}
      <ul className="m-0 list-none space-y-1 p-0 text-sm">
        {data.history.slice(0, 25).map((entry) => (
          <li key={entry.id}>
            <span className="text-content-secondary">
              {formatDateTime(entry.occurredAt)}
            </span>{' '}
            <strong>{entry.action.replace(/_/g, ' ')}</strong>
            {entry.entityLabel ? ` · ${entry.entityLabel}` : ''}
          </li>
        ))}
      </ul>
    </Panel>
  );
}
